import { useEffect, useState } from 'react';

/**
 * Latches to true once the session deadline (minus the threshold) passes.
 * setTimeout truncates its delay to a signed 32-bit int (~24.8 days max), so
 * far-future expiries wrap — sometimes negative, firing "expired" at boot.
 * Cap each arm and re-check on fire instead of trusting one long timer; and
 * re-check on window focus, as setTimeout might be paused in some browsers
 * while the tab is inactive.
 */
export function useSessionExpiry(
    expiresAt: Date | null,
    { thresholdMs = 60_000 }: { thresholdMs?: number } = {},
): boolean {
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        const MAX_DELAY = 2 ** 31 - 1;
        let timeout: ReturnType<typeof setTimeout>;
        const arm = () => {
            const expiresIn = expiresAt
                ? new Date(expiresAt).getTime() - Date.now() - thresholdMs
                : Infinity;

            if (!Number.isFinite(expiresIn)) return;
            else if (expiresIn <= 0) {
                setIsExpired(true);
                return;
            }
            timeout = setTimeout(arm, Math.min(expiresIn, MAX_DELAY));
        };
        arm();
        return () => clearTimeout(timeout);
    }, [expiresAt, thresholdMs]);

    useEffect(() => {
        const onFocus = () => {
            if (!expiresAt) return;
            const exp = new Date(expiresAt).getTime() - thresholdMs;
            if (Date.now() >= exp) setIsExpired(true);
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [expiresAt, thresholdMs]);

    return isExpired;
}
